import { supabaseClient } from "../../services/supabase";

import type {
  AgendaState,
  Booking,
  FormaPagamentoQuadra,
  PagamentoReservaResult,
} from "./types";

type CourtRow = {
  id: string;

  nome: string;

  ativa: boolean;
};

type ReservaRpcResult = {
  success: boolean;
  reserva: BookingRow;
};

type BookingRow = {
  id: string;

  quadra_id: string | null;

  data: string;

  hora: number;

  duracao: number;

  cliente: string;

  cliente_id: string | null;

  valor: number;

  quantidade_pessoas: number;

  pago: boolean;

  forma_pagamento:
    FormaPagamentoQuadra | null;

  pago_em: string | null;

  created_at: string;

  updated_at: string | null;
};


export async function loadAgenda(): Promise<AgendaState> {
  /*
   * =======================================================
   * 1. QUADRAS ATIVAS
   * =======================================================
   */

  const {
  data: courtsData,
  error: courtsError,
} =
  await supabaseClient
    .from("quadras")
    .select(
      "id,nome,ativa",
    )
    .eq(
      "ativa",
      true,
    )
    .order(
      "created_at",
      {
        ascending: true,
      },
    );

  if (courtsError) {
    throw courtsError;
  }

  const courts =
    (courtsData ??
      []) as CourtRow[];

  const courtNameById =
    new Map<
      string,
      string
    >();

  courts.forEach(
    (court) => {
      courtNameById.set(
        court.id,
        court.nome,
      );
    },
  );

  /*
   * =======================================================
   * 2. RESERVAS
   * =======================================================
   */

  const {
    data: bookingsData,
    error: bookingsError,
  } =
    await supabaseClient
      .from(
        "reservas_quadras",
      )
      .select(
  `
    id,
    quadra_id,
    data,
    hora,
    duracao,
    cliente,
    cliente_id,
    valor,
    quantidade_pessoas,
    pago,
    forma_pagamento,
    pago_em,
    created_at,
    updated_at
  `,
)
      .order(
        "data",
        {
          ascending: true,
        },
      )
      .order(
        "hora",
        {
          ascending: true,
        },
      );

  if (bookingsError) {
    throw bookingsError;
  }

  const bookingRows =
    (bookingsData ??
      []) as BookingRow[];

  /*
   * =======================================================
   * 3. BANCO -> BOOKING
   * =======================================================
   */

  const bookings =
    bookingRows
      .map<Booking | null>(
        (row) => {
          const court =
            row.quadra_id
              ? courtNameById.get(
                  row.quadra_id,
                )
              : undefined;

          /*
           * Reservas vinculadas a quadras
           * inativas não entram na Agenda
           * operacional.
           */

          if (!court) {
            return null;
          }

          const booking:
            Booking = {
              id:
                row.id,

                kind: "reserva",

              date:
                row.data,

              court,

              hour:
                Number(
                  row.hora,
                ),

              duration:
                Number(
                  row.duracao,
                ),

              cliente:
                row.cliente,

              cliente_id:
                row.cliente_id,

                updated_at:
  row.updated_at,

              price:
                Number(
                  row.valor,
                ),

              pago:
                Boolean(
                  row.pago,
                ),

              forma_pagamento:
                row.forma_pagamento,

              pago_em:
                row.pago_em,
            };

          return booking;
        },
      )
      .filter(
        (
          booking,
        ): booking is Booking =>
          booking !== null,
      );

  return {
    courts:
      courts.map(
        (court) =>
          court.nome,
      ),

    bookings,
  };
}

export async function criarReservaQuadra(input: {
  court: string;
  data: string;
  hora: number;
  duracao: number;
  cliente: string;
  clienteId?: string | null;
  quantidadePessoas?: number;
}) {
  /*
   * =====================================================
   * 1. DESCOBRE O ID DA QUADRA PELO NOME
   * =====================================================
   *
   * O React trabalha com o nome da quadra.
   * A RPC trabalha com o UUID.
   */

  const {
    data: quadra,
    error: quadraError,
  } =
    await supabaseClient
      .from("quadras")
      .select("id")
      .eq(
        "nome",
        input.court,
      )
      .eq(
        "ativa",
        true,
      )
      .single();


  if (quadraError) {
    throw quadraError;
  }


  if (!quadra?.id) {
    throw new Error(
      "Quadra não encontrada ou inativa.",
    );
  }


  /*
   * =====================================================
   * 2. CRIA A RESERVA PELA RPC
   * =====================================================
   */

  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "criar_reserva_quadra",
      {
        p_quadra_id:
          quadra.id,

        p_data:
          input.data,

        p_hora:
          input.hora,

        p_duracao:
          input.duracao,

        p_cliente:
          input.cliente,

        p_cliente_id:
          input.clienteId ??
          null,

        p_quantidade_pessoas:
          input.quantidadePessoas ??
          1,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "A criação da reserva não retornou dados.",
    );
  }


  return data as ReservaRpcResult;
}


export async function editarReservaQuadra(input: {
  reservaId: string;
  cliente: string;
  clienteId?: string | null;
  duracao: number;
  valor: number;
  updatedAtEsperado?: string | null;
}) {
  const { data, error } =
    await supabaseClient.rpc(
      "editar_reserva_quadra_com_valor",
      {
        p_reserva_id:
          input.reservaId,

        p_cliente:
          input.cliente,

        p_cliente_id:
          input.clienteId ??
          null,

        p_duracao:
          input.duracao,

        p_valor:
          input.valor,

        p_updated_at_esperado:
        input.updatedAtEsperado ??
        null,
      },
    );

  if (error) {
    throw error;
  }

  return data as ReservaRpcResult;
}


export async function cancelarReservaQuadra(
  reservaId: string,
) {
  const { data, error } =
    await supabaseClient.rpc(
      "cancelar_reserva_quadra",
      {
        p_reserva_id:
          reservaId,
      },
    );

  if (error) {
    throw error;
  }

  return data as ReservaRpcResult;
}


export async function restaurarReservaQuadra(
  reserva: Booking,
) {
  /*
   * =====================================================
   * 1. DESCOBRE O ID DA QUADRA PELO NOME
   * =====================================================
   */

  const {
    data: quadra,
    error: quadraError,
  } =
    await supabaseClient
      .from("quadras")
      .select("id")
      .eq(
        "nome",
        reserva.court,
      )
      .eq(
        "ativa",
        true,
      )
      .single();


  if (quadraError) {
    throw quadraError;
  }


  if (!quadra?.id) {
    throw new Error(
      "Quadra não encontrada ou inativa.",
    );
  }


  /*
   * =====================================================
   * 2. MONTA O FORMATO ESPERADO PELA RPC
   * =====================================================
   */

  const reservaParaRestaurar = {
    id:
      reserva.id,

    quadra_id:
      quadra.id,

    data:
      reserva.date,

    hora:
      reserva.hour,

    duracao:
      reserva.duration,

    cliente:
      reserva.cliente,

    cliente_id:
      reserva.cliente_id ??
      null,

    valor:
      reserva.price ??
      0,

    quantidade_pessoas:
      1,

    pago:
      false,

    forma_pagamento:
      null,

    pago_em:
      null,
  };


  /*
   * =====================================================
   * 3. RESTAURA PELA RPC
   * =====================================================
   */

  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "restaurar_reserva_quadra",
      {
        p_reserva:
          reservaParaRestaurar,
      },
    );


  if (error) {
    throw error;
  }


  if (!data) {
    throw new Error(
      "A restauração da reserva não retornou dados.",
    );
  }


  return data as ReservaRpcResult;
}
/*
 * =========================================================
 * PAGAMENTO DE RESERVA
 * =========================================================
 *
 * A alteração da reserva e o lançamento
 * em movimentos_caixa acontecem dentro
 * da RPC PostgreSQL.
 *
 * Assim não existe o risco de:
 *
 * reserva = paga
 * caixa   = sem lançamento
 *
 * ou o contrário.
 * =========================================================
 */

export async function pagarReservaQuadra(
  reservaId: string,
  formaPagamento:
    FormaPagamentoQuadra,
): Promise<PagamentoReservaResult> {
  const {
    data,
    error,
  } =
    await supabaseClient.rpc(
      "pagar_reserva_quadra",
      {
        p_reserva_id:
          reservaId,

        p_forma_pagamento:
          formaPagamento,
      },
    );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "O pagamento não retornou os dados da reserva.",
    );
  }

  return data as PagamentoReservaResult;
}

export type ParcelaReserva = {
  id: string;
  reserva_id: string;
  nome: string;
  valor_previsto: number;
  valor_pago: number;
  status: "pendente" | "pago" | "desconto";
  forma_pagamento: FormaPagamentoQuadra | null;
  pago_em: string | null;
  ordem: number;
};

export type ResumoPagamentoReserva = {
  parcelas: ParcelaReserva[];
  total: number;
  recebido: number;
  desconto: number;
  saldo: number;
  saldo_destino: "divida" | "desconto" | null;
};

export type CorrecaoParcelaReserva = {
  id?: string | null;
  nome: string;
  valor_previsto: number;
  valor_pago: number;
  status: "pendente" | "pago" | "desconto";
  forma_pagamento: FormaPagamentoQuadra | null;
};

export type CorrecaoPagamentoReservaResult = {
  success: boolean;
  reserva: BookingRow;
  resumo: ResumoPagamentoReserva;
  movimentos_arquivados: number;
};

export async function loadPagamentosReserva(
  reservaId: string,
): Promise<ResumoPagamentoReserva> {
  const { data, error } =
    await supabaseClient.rpc(
      "resumo_pagamentos_reserva",
      { p_reserva_id: reservaId },
    );

  if (error) throw error;
  return data as ResumoPagamentoReserva;
}

export async function configurarDivisaoReserva(
  reservaId: string,
  nomes: string[],
): Promise<ResumoPagamentoReserva> {
  const { data, error } =
    await supabaseClient.rpc(
      "configurar_divisao_reserva",
      {
        p_reserva_id: reservaId,
        p_nomes: nomes,
      },
    );

  if (error) throw error;
  return data as ResumoPagamentoReserva;
}

export async function receberParcelaReserva(
  parcelaId: string,
  formaPagamento: FormaPagamentoQuadra,
): Promise<ResumoPagamentoReserva> {
  const { data, error } =
    await supabaseClient.rpc(
      "receber_parcela_reserva",
      {
        p_parcela_id: parcelaId,
        p_forma_pagamento: formaPagamento,
      },
    );

  if (error) throw error;
  return data as ResumoPagamentoReserva;
}

export async function receberTodasParcelasReserva(
  reservaId: string,
  formaPagamento: FormaPagamentoQuadra,
): Promise<ResumoPagamentoReserva> {
  const { data, error } =
    await supabaseClient.rpc(
      "receber_todas_parcelas_reserva",
      {
        p_reserva_id: reservaId,
        p_forma_pagamento: formaPagamento,
      },
    );

  if (error) throw error;
  return data as ResumoPagamentoReserva;
}

export async function encerrarSaldoReserva(
  reservaId: string,
  destino: "divida" | "desconto",
): Promise<ResumoPagamentoReserva> {
  const { data, error } =
    await supabaseClient.rpc(
      "encerrar_saldo_reserva",
      {
        p_reserva_id: reservaId,
        p_destino: destino,
      },
    );

  if (error) throw error;
  return data as ResumoPagamentoReserva;
}

export async function corrigirPagamentosReserva(input: {
  reservaId: string;
  valorTotal: number;
  motivo: string;
  parcelas: CorrecaoParcelaReserva[];
}): Promise<CorrecaoPagamentoReservaResult> {
  const { data, error } = await supabaseClient.rpc(
    "corrigir_pagamentos_reserva",
    {
      p_reserva_id: input.reservaId,
      p_valor_total: input.valorTotal,
      p_motivo: input.motivo,
      p_parcelas: input.parcelas,
    },
  );

  if (error) throw error;
  return data as CorrecaoPagamentoReservaResult;
}
type RecurringLessonRow = {
  id: string;

  nome: string;

  professor: string | null;

  dias: string[];

  hora: string;

  duracao: number;

  tipo:
    | "arena"
    | "externa";

  quadra_id:
    string | null;

  ativa: boolean;

  bloquear_quadra: boolean;
};


const DAY_NAMES = [
  "Dom",
  "Seg",
  "Ter",
  "Qua",
  "Qui",
  "Sex",
  "Sáb",
] as const;


function timeToNumber(
  value: string,
) {
  const [
    hours,
    minutes,
  ] =
    value
      .split(":")
      .map(Number);

  return (
    hours +
    (minutes || 0) / 60
  );
}


export async function loadRecurringLessons(
  date: string,
): Promise<Booking[]> {
  /*
   * =====================================================
   * AULAS RECORRENTES DA DATA
   * =====================================================
   *
   * Estas aulas NÃO são gravadas em reservas_quadras.
   *
   * Elas existem somente como bloqueios virtuais
   * para a Agenda.
   * =====================================================
   */

  const selectedDate =
    new Date(
      `${date}T12:00:00`,
    );


  const dayName =
    DAY_NAMES[
      selectedDate.getDay()
    ];


  const {
    data:
      lessonsData,

    error:
      lessonsError,
  } =
    await supabaseClient
      .from("turmas")
      .select(
        `
          id,
          nome,
          professor,
          dias,
          hora,
          duracao,
          tipo,
          quadra_id,
          ativa,
          bloquear_quadra
        `,
      )
      .eq(
        "ativa",
        true,
      )
      .eq(
        "bloquear_quadra",
        true,
      )
      .contains(
        "dias",
        [dayName],
      );


  if (lessonsError) {
    throw lessonsError;
  }


  const lessons =
    (lessonsData ??
      []) as RecurringLessonRow[];


  if (
    lessons.length ===
    0
  ) {
    return [];
  }


  /*
   * Precisamos dos nomes das quadras,
   * pois a Agenda trabalha pelo nome.
   */

  const courtIds =
    Array.from(
      new Set(
        lessons
          .map(
            (lesson) =>
              lesson.quadra_id,
          )
          .filter(
            (
              id,
            ): id is string =>
              Boolean(id),
          ),
      ),
    );


  if (
    courtIds.length ===
    0
  ) {
    return [];
  }


  const {
    data:
      courtsData,

    error:
      courtsError,
  } =
    await supabaseClient
      .from("quadras")
      .select(
        "id,nome,ativa",
      )
      .in(
        "id",
        courtIds,
      )
      .eq(
        "ativa",
        true,
      );


  if (courtsError) {
    throw courtsError;
  }


  const courtNameById =
    new Map<
      string,
      string
    >();


  (
    courtsData ??
    []
  ).forEach(
    (court) => {
      courtNameById.set(
        String(
          court.id,
        ),

        String(
          court.nome,
        ),
      );
    },
  );


  return lessons
    .map<Booking | null>(
      (lesson) => {
        if (
          !lesson.quadra_id
        ) {
          return null;
        }


        const court =
          courtNameById.get(
            lesson.quadra_id,
          );


        if (!court) {
          return null;
        }


        return {
          /*
           * ID virtual.
           *
           * Não é UUID de reservas_quadras
           * e nunca será salvo lá.
           */
          id:
            `aula-${lesson.id}-${date}`,

          kind:
            "aula",

          date,

          court,

          hour:
            timeToNumber(
              lesson.hora,
            ),

          duration:
            Number(
              lesson.duracao,
            ),

          /*
           * Mantemos cliente preenchido
           * porque alguns componentes
           * trabalham com esse campo.
           */
          cliente:
            lesson.nome,

          cliente_id:
            null,

          turma_id:
            lesson.id,

          turma_nome:
            lesson.nome,

          professor:
            lesson.professor,

          tipo_turma:
            lesson.tipo,

          /*
           * Aula não é aluguel de quadra.
           */
          price:
            undefined,

          pago:
            undefined,

          forma_pagamento:
            null,

          pago_em:
            null,
        };
      },
    )
    .filter(
      (
        lesson,
      ): lesson is Booking =>
        lesson !== null,
    );
}
